// "Sales price markup" (flag REACT_APP_PRICE_MARKUP_ENABLED, FORSLAG-AVANCE-
// PROCENT 15/10 variant A): pure display math, no React, no data calls.
//
// Rules (all display-only - nothing here is ever sent to the API):
// - sales price = purchase price x (1 + pct/100), the API's AddCommission
//   formula (PriceAdjustmentService) and the FE replica applyCommissionAmount.
// - A customer-specific commission wins over the general %: when the API
//   already sent a customer price that differs from the purchase price
//   (adjustedCustomerPrice / adjustedUnitPrice), THAT is the dealer's sales
//   price for this customer and the % is not applied on top (no "markup on
//   markup"). Equal or missing customer price = no commission = the %.
// - Sign-preserving: a negative FromStorage amount x 1.5 stays negative.
// - Missing / non-finite amounts pass through untouched (pending products,
//   "Image on its way", empty price rows render exactly as today).
// - Rounding happens at the render site (toFixed(2)); totals are summed on
//   the unrounded values and rounded last. roundSalesPrice is for the few
//   places that print a raw number without toFixed.

export interface SalesMarkup {
  enabled: boolean;
  /** 0-500, at most 2 decimals. */
  percent: number;
}

export const SALES_MARKUP_OFF: SalesMarkup = Object.freeze({
  enabled: false,
  percent: 0,
});

export const SALES_MARKUP_MAX_PERCENT = 500;

export function salesMarkupFactor(markup: SalesMarkup | undefined): number {
  if (!markup?.enabled) return 1;
  const pct = Number(markup.percent);
  if (!Number.isFinite(pct)) return 1;
  return 1 + pct / 100;
}

/**
 * True when the API sent a customer price that differs from the purchase
 * price = a customer-specific commission exists for this row.
 */
export function hasCustomerCommission(
  amount: number | null | undefined,
  customerAmount: number | null | undefined,
): boolean {
  if (customerAmount === undefined || customerAmount === null) return false;
  if (!Number.isFinite(customerAmount) || customerAmount === 0) return false;
  if (amount === undefined || amount === null) return false;
  return customerAmount !== amount;
}

/** The unit/line amount to SHOW for a purchase-price amount. */
export function salesPrice(
  amount: number,
  markup: SalesMarkup | undefined,
  customerAmount?: number | null,
): number;
export function salesPrice(
  amount: number | undefined,
  markup: SalesMarkup | undefined,
  customerAmount?: number | null,
): number | undefined;
export function salesPrice(
  amount: number | null | undefined,
  markup: SalesMarkup | undefined,
  customerAmount?: number | null,
): number | null | undefined {
  if (!markup?.enabled) return amount;
  if (amount === undefined || amount === null) return amount;
  if (typeof amount !== "number" || !Number.isFinite(amount)) return amount;
  if (hasCustomerCommission(amount, customerAmount)) return customerAmount!;
  return amount * salesMarkupFactor(markup);
}

/**
 * A line total (qty x unit) to SHOW, given the unit prices the total was
 * built from: with a customer commission the total scales by the same
 * ratio the unit did (customer unit / purchase unit), otherwise by the %.
 */
export function salesLineTotal(
  lineTotal: number,
  markup: SalesMarkup | undefined,
  unitPrice?: number | null,
  customerUnitPrice?: number | null,
): number {
  if (!markup?.enabled) return lineTotal;
  if (!Number.isFinite(lineTotal)) return lineTotal;
  if (
    hasCustomerCommission(unitPrice, customerUnitPrice) &&
    unitPrice &&
    Number.isFinite(unitPrice)
  ) {
    return lineTotal * (customerUnitPrice! / unitPrice);
  }
  return lineTotal * salesMarkupFactor(markup);
}

export interface SalesTotalRow {
  lineTotal: number;
  unit?: number | null;
  customerUnit?: number | null;
}

/**
 * A summed total to SHOW: ONE factor on the (unrounded) sum when no row
 * carries a customer commission (the common case - the factor is the same on
 * every line, so sum x factor == sum of marked-up lines); otherwise the rows
 * are re-summed with each row scaled like its own unit price.
 */
export function salesTotal(
  total: number,
  markup: SalesMarkup | undefined,
  rows: SalesTotalRow[],
): number {
  if (!markup?.enabled) return total;
  if (!Number.isFinite(total)) return total;
  if (!rows.some((r) => hasCustomerCommission(r.unit, r.customerUnit))) {
    return total * salesMarkupFactor(markup);
  }
  return rows.reduce(
    (acc, r) =>
      acc + salesLineTotal(r.lineTotal || 0, markup, r.unit, r.customerUnit),
    0,
  );
}

/** For raw prints without toFixed: 2 decimals, half-up on the cent. */
export function roundSalesPrice(amount: number): number {
  if (!Number.isFinite(amount)) return amount;
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

/**
 * 0-500 with at most 2 decimals (the API's 400 rule,
 * VendorDisplayMarkupPolicy: Decimal.Round(p, 2) == p). Compared on the
 * cent with a tolerance: in binary floating point 1.15 * 100 is
 * 114.99999999999999, so an exact `Math.round(p * 100) === p * 100` rejected
 * 1.15, 0.29, 4.35, 8.2, 2.55, 0.57 ... - valid 2-decimal percents the API
 * accepts. 1e-6 of a cent is far below any 3rd-decimal remainder (>= 0.1).
 */
export function isValidMarkupPercent(percent: unknown): percent is number {
  if (typeof percent !== "number" || !Number.isFinite(percent)) return false;
  if (percent < 0 || percent > SALES_MARKUP_MAX_PERCENT) return false;
  const cents = percent * 100;
  return Math.abs(cents - Math.round(cents)) < 1e-6;
}

export interface SalesTotals {
  subTotal: number;
  vat: number;
  total: number;
}

/**
 * The API's stored order totals (Orders.SubTotal/VAT/Total) to SHOW next to
 * rows that went through salesPrice. The stored sum is NOT always a
 * purchase-price sum: OrderTotalsRule prices a CUSTOMER-org order at
 * Σ qty x CustomerPrice (the customer basis - a commission already in it),
 * and only a vendor-org order at Σ qty x (agreed ?? VendorPrice). One flat
 * factor on a customer-basis sum would be "markup on markup" and put the
 * totals row a whole factor away from the rows' sum. So: no row with a
 * customer commission (the common case) = one factor on each stored amount,
 * exactly as the rows; otherwise the rows are re-summed each on its own
 * basis (salesTotal) and VAT/total follow in the same ratio, so the totals
 * row always equals the sum of the rows shown above it. A missing / zero
 * stored subtotal (a draft) keeps the flat factor (0 stays 0). Off = input.
 */
export function salesOrderTotals(
  totals: SalesTotals,
  markup: SalesMarkup | undefined,
  rows: SalesTotalRow[],
): SalesTotals {
  if (!markup?.enabled) return totals;
  const factor = salesMarkupFactor(markup);
  if (
    !totals.subTotal ||
    !Number.isFinite(totals.subTotal) ||
    !rows.some((r) => hasCustomerCommission(r.unit, r.customerUnit))
  ) {
    return {
      subTotal: totals.subTotal * factor,
      vat: totals.vat * factor,
      total: totals.total * factor,
    };
  }
  const subTotal = salesTotal(totals.subTotal, markup, rows);
  const ratio = subTotal / totals.subTotal;
  const vat = (totals.vat || 0) * ratio;
  return { subTotal, vat, total: subTotal + vat };
}

/** The slice of a stored order line the totals math needs. */
export interface SalesOrderLineLike {
  /** Stored line amount: unitPrice x qty on a placed order. */
  price: number;
  unitPrice: number;
  adjustedUnitPrice: number;
  quantity: number;
  product?: { agreedUnitPrice?: number | null } | null;
}

/**
 * salesTotal rows for a PLACED order's lines (price = line total). With the
 * admin price adjustment in play (agreedApplies) a line's amount is
 * qty x agreed price - the number the stored totals were built from and the
 * number the row shows - scaled like its own unit price (customer commission
 * ratio or the %), exactly as useOrderLinePrice scales the agreed price.
 */
export function placedOrderSalesRows(
  lines: SalesOrderLineLike[] | undefined,
  agreedApplies: boolean,
): SalesTotalRow[] {
  return (lines ?? []).map((line) => {
    const agreed = agreedApplies ? line.product?.agreedUnitPrice : undefined;
    const lineTotal =
      agreed !== undefined && agreed !== null
        ? agreed * (line.quantity || 0)
        : line.price;
    return {
      lineTotal,
      unit: line.unitPrice,
      customerUnit: line.adjustedUnitPrice,
    };
  });
}
